import { useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import Markdown from "react-markdown";
import { api, errMsg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { AuthShell } from "@/components/AuthShell";
import { Field, PasswordInput, PasswordRules, PrimaryButton, PW_RULES } from "@/components/FormBits";

export function ForgotPasswordPage() {
  const [email, setEmail] = useState("");
  const [state, setState] = useState({ busy: false, done: "", error: "" });
  const submit = async (e) => {
    e.preventDefault();
    setState({ busy: true, done: "", error: "" });
    try {
      const { data } = await api.post("/auth/forgot-password", { email });
      setState({ busy: false, done: data.message, error: "" });
    } catch (err) {
      setState({ busy: false, done: "", error: errMsg(err) });
    }
  };
  return (
    <AuthShell title="Reset your password" sub="Enter your account email and we'll send a secure reset link."
      footer={<Link to="/signin" data-testid="forgot-back-signin" className="font-semibold text-[#2C1810] underline decoration-[#C5A059] underline-offset-4">Back to sign in</Link>}>
      <form onSubmit={submit} className="space-y-5" data-testid="forgot-form">
        <Field label="Email address" id="forgot-email-input" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} />
        {state.done && <p role="status" data-testid="forgot-success" className="rounded-xl bg-[#EAF3ED] px-4 py-3 text-sm text-[#2E6B4F]">{state.done}</p>}
        {state.error && <p role="alert" data-testid="forgot-error" className="rounded-xl bg-[#F9ECEC] px-4 py-3 text-sm text-[#8C2A2A]">{state.error}</p>}
        <PrimaryButton type="submit" disabled={state.busy} data-testid="forgot-submit-btn" className="w-full">{state.busy ? "Sending…" : "Send reset link"}</PrimaryButton>
      </form>
    </AuthShell>
  );
}

export function ResetPasswordPage() {
  const [params] = useSearchParams();
  const [pw, setPw] = useState("");
  const [state, setState] = useState({ busy: false, done: false, error: "" });
  const ok = PW_RULES.every(([r]) => r(pw));
  const submit = async (e) => {
    e.preventDefault();
    if (!ok) return setState({ ...state, error: "Please choose a password that meets all requirements." });
    setState({ busy: true, done: false, error: "" });
    try {
      await api.post("/auth/reset-password", { token: params.get("token") || "", password: pw });
      setState({ busy: false, done: true, error: "" });
    } catch (err) {
      setState({ busy: false, done: false, error: errMsg(err) });
    }
  };
  if (state.done) {
    return (
      <AuthShell title="Password updated" sub="Your new password is ready to use.">
        <Link to="/signin" data-testid="reset-go-signin" className="inline-flex min-h-[48px] w-full items-center justify-center rounded-full bg-[#2C1810] text-sm font-semibold text-[#FDFBF7]">Sign in</Link>
      </AuthShell>
    );
  }
  return (
    <AuthShell title="Choose a new password">
      <form onSubmit={submit} className="space-y-5" data-testid="reset-form">
        <div>
          <label htmlFor="reset-password-input" className="text-[11px] font-semibold uppercase tracking-[0.18em] text-[#6E5549]">New password</label>
          <div className="mt-1.5"><PasswordInput id="reset-password-input" autoComplete="new-password" value={pw} onChange={(e) => setPw(e.target.value)} /></div>
          <PasswordRules password={pw} />
        </div>
        {state.error && <p role="alert" data-testid="reset-error" className="rounded-xl bg-[#F9ECEC] px-4 py-3 text-sm text-[#8C2A2A]">{state.error}</p>}
        <PrimaryButton type="submit" disabled={state.busy} data-testid="reset-submit-btn" className="w-full">{state.busy ? "Saving…" : "Update password"}</PrimaryButton>
      </form>
    </AuthShell>
  );
}

export function VerifyEmailPage() {
  const [params] = useSearchParams();
  const { user, refreshUser } = useAuth();
  const [state, setState] = useState({ status: "working", error: "" });
  const ran = useRef(false);
  useEffect(() => {
    if (ran.current) return;
    ran.current = true;
    api.post("/auth/verify-email", { token: params.get("token") || "" })
      .then(async () => {
        setState({ status: "done", error: "" });
        if (user) await refreshUser().catch(() => {});
      })
      .catch((err) => setState({ status: "error", error: errMsg(err) }));
  }, [params, user, refreshUser]);
  const next = user ? "/" : "/signin";
  return (
    <AuthShell title={state.status === "done" ? "Email confirmed" : state.status === "error" ? "Link not valid" : "Confirming your email…"}>
      {state.status === "done" && <p data-testid="verify-success" className="rounded-xl bg-[#EAF3ED] px-4 py-3 text-sm text-[#2E6B4F]">Thank you — your email address is verified.</p>}
      {state.status === "error" && <p data-testid="verify-error" className="rounded-xl bg-[#F9ECEC] px-4 py-3 text-sm text-[#8C2A2A]">{state.error} You can request a new link from your dashboard after signing in.</p>}
      {state.status !== "working" && <Link to={next} data-testid="verify-continue" className="mt-6 inline-flex min-h-[48px] w-full items-center justify-center rounded-full bg-[#2C1810] text-sm font-semibold text-[#FDFBF7]">Continue</Link>}
    </AuthShell>
  );
}

export function LegalPage({ kind }) {
  const title = kind === "terms" ? "Terms of Use" : "Privacy Policy";
  const [doc, setDoc] = useState(null);
  const [error, setError] = useState("");
  useEffect(() => {
    api.get(`/legal/${kind}`).then(({ data }) => setDoc(data)).catch((e) => setError(errMsg(e)));
  }, [kind]);
  return (
    <div className="min-h-screen bg-[#FDFBF7] px-6 py-8 sm:px-10">
      <div className="mx-auto w-full max-w-3xl">
        <Link to="/welcome" className="text-xs font-semibold uppercase tracking-[0.2em] text-[#9C6218] hover:underline">← Ink Genius</Link>
        <h1 className="mt-6 text-4xl font-semibold text-[#2C1810] sm:text-5xl">{title}</h1>
        {doc?.effective_date && <p className="mt-2 text-sm text-[#6E5549]" data-testid={`legal-${kind}-effective`}>Effective {doc.effective_date}</p>}
        <div className="mt-8">
          {error && <p role="alert" className="rounded-xl bg-[#F9ECEC] px-4 py-3 text-sm text-[#8C2A2A]">We couldn't load this page. Please try again.</p>}
          {!doc && !error && <p className="text-sm text-[#A88B7D]">Loading…</p>}
          {doc && doc.body && <article className="ig-prose text-[15px] text-[#4A3B32]" data-testid={`legal-${kind}-body`}><Markdown>{doc.body}</Markdown></article>}
          {doc && !doc.body && (
            <div data-testid={`legal-${kind}-placeholder`} className="rounded-2xl border border-dashed border-[#9C6218] bg-[#FAF2E6] p-6 text-sm leading-relaxed text-[#4A3B32]">
              <p className="font-semibold text-[#9C6218]">Placeholder — launch requirement</p>
              <p className="mt-2">The approved MooreBeautyLove {title} has not been published yet. This page must be replaced with the final, legally reviewed document before public launch.</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export function ConfirmEmailChangePage() {
  const [params] = useSearchParams();
  const { user, logout } = useAuth();
  const [state, setState] = useState({ status: "working", text: "" });
  const ran = useRef(false);
  useEffect(() => {
    if (ran.current) return;
    ran.current = true;
    api.post("/auth/confirm-email-change", { token: params.get("token") || "" })
      .then(async ({ data }) => {
        setState({ status: "done", text: data.message });
        if (user) await logout().catch(() => {});
      })
      .catch((err) => setState({ status: "error", text: errMsg(err) }));
  }, [params, user, logout]);
  return (
    <AuthShell title={state.status === "done" ? "Email updated" : state.status === "error" ? "Link not valid" : "Updating your email…"}>
      {state.status === "done" && <p data-testid="email-change-success" className="rounded-xl bg-[#EAF3ED] px-4 py-3 text-sm text-[#2E6B4F]">{state.text}</p>}
      {state.status === "error" && <p data-testid="email-change-error" className="rounded-xl bg-[#F9ECEC] px-4 py-3 text-sm text-[#8C2A2A]">{state.text} You can request a new link from My Plan.</p>}
      {state.status !== "working" && <Link to="/signin" data-testid="email-change-continue" className="mt-6 inline-flex min-h-[48px] w-full items-center justify-center rounded-full bg-[#2C1810] text-sm font-semibold text-[#FDFBF7]">Sign in</Link>}
    </AuthShell>
  );
}
